import React from 'react';
import HeroSection from '../components/HeroSection';
import SEO from '../components/SEO';

const HERO_IMG = '/images/heritage-brass-band.jpg';

// Businesses and sponsors who have supported Glossop & District Round Table
// events (Bonfire Night, Heritage Weekend, GlossFest, auctions and raffles).
// Order is roughly by category then alphabetical. Edit descriptions here when
// a sponsor's offering changes — keep each one to 2 short sentences max.
const SUPPORTERS = [
  {
    name: 'MBC Building Contractors NW',
    tag: 'Construction',
    desc: 'North-West building contractors delivering commercial, industrial and residential projects. Long-standing supporters of Round Table community events.',
    url: 'https://www.mbcbuilding.co.uk/',
  },
  {
    name: 'Foxx Building Services Group',
    tag: 'Construction',
    desc: 'Local building services group covering new builds, extensions, refurbishments and property maintenance across Glossop and the wider North-West.',
    url: 'https://foxxgroup.co.uk/',
  },
  {
    name: 'Torside Building Consultants',
    tag: 'Construction',
    desc: 'Independent building consultancy providing surveys, project management and specialist advice to homeowners and businesses across the Peak District.',
    url: 'https://torside.co.uk/',
  },
  {
    name: 'Gas Care',
    tag: 'Trades · Heating',
    desc: 'Gas Safe-registered heating and plumbing specialists serving Glossop and the surrounding area — boilers, servicing, repairs and installations.',
    url: 'https://www.gas.care/',
  },
  {
    name: 'Glossop Gasworks',
    tag: 'Business Hub · Workspace',
    desc: 'Iconic Glossop town-centre building offering serviced offices, hot desks, flexible workspace, meeting room hire and virtual office addresses. Full-fibre internet, on-site parking and under five minutes from the railway station.',
    url: 'https://www.glossopgasworks.co.uk/',
  },
  {
    name: 'Davis Blank Furniss',
    tag: 'Professional · Legal',
    desc: 'Full-service solicitors with Manchester and Glossop offices, covering commercial, property, family and private-client law for businesses and individuals.',
    url: 'https://dbf-law.co.uk/',
  },
  {
    name: 'Rhodes & Partners',
    tag: 'Professional · Engineering',
    desc: 'Structural and civil engineering consultancy based in Marple, Greater Manchester. A chartered team designing structures and site-specific foundations for new builds and refurbishments — established by Richard Rhodes in 1992.',
    url: 'https://www.rhodesandpartners.co.uk/',
  },
  {
    name: 'Binary Consultancy',
    tag: 'Professional · IT',
    desc: 'IT consultancy and managed-service provider working with businesses across the North-West on infrastructure, cyber security and cloud transformation.',
    url: 'https://www.bincon.net/',
  },
  {
    name: 'Karsons Lettings',
    tag: 'Property',
    desc: 'Independent letting agents specialising in residential rentals across Glossop, Hadfield, Hyde and the wider Tameside and High Peak area.',
    url: 'https://www.karsonslettings.com/',
  },
  {
    name: 'Glossop Caravans',
    tag: 'Automotive · Leisure',
    desc: 'Family-run caravan and motorhome dealership on the edge of Glossop — sales, servicing, accessories and winter storage for touring caravans and motorhomes.',
    url: 'https://www.glossopcaravans.co.uk/',
  },
  {
    name: 'Practical Car & Van Hire',
    tag: 'Automotive · Hire',
    desc: 'Glossop branch of Practical Car & Van Hire, providing short and long-term car and van rental for home moves, trade and business use at competitive local rates.',
    url: 'https://www.practical.co.uk/',
  },
];

function SupporterCard({ supporter }) {
  const slug = supporter.name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
  return (
    <article
      data-testid={`supporter-card-${slug}`}
      className="bg-white border border-zinc-200 p-6 sm:p-7 flex flex-col hover:border-[#F0A500] transition-colors"
    >
      <div className="flex items-start justify-between gap-4 mb-3">
        <h2 className="text-xl font-bold tracking-tight text-[#0A0A0A]">{supporter.name}</h2>
        <span className="text-[10px] uppercase tracking-widest font-bold text-[#F0A500] whitespace-nowrap mt-1">
          {supporter.tag}
        </span>
      </div>
      <p className="text-sm text-[#52525B] leading-relaxed flex-1">{supporter.desc}</p>
      {supporter.url && (
        <a
          href={supporter.url}
          target="_blank"
          rel="noopener noreferrer"
          data-testid={`supporter-link-${slug}`}
          className="mt-5 text-sm font-bold uppercase tracking-wider text-[#F0A500] hover:text-[#D99500] self-start"
        >
          Visit website &rarr;
        </a>
      )}
    </article>
  );
}

export default function SupportersPage() {
  return (
    <div data-testid="supporters-page">
      <SEO
        title="Our Supporters"
        description="The local businesses and sponsors backing Glossop & District Round Table 916 events — Bonfire Night, Heritage Weekend, GlossFest and more."
        path="/supporters"
        keywords={['Glossop Round Table sponsors', 'Glossop Bonfire sponsors', 'Glossop business community', 'local supporters Glossop']}
      />

      <HeroSection
        image={HERO_IMG}
        title="Our Supporters"
        subtitle="The local businesses that make our events — and our fundraising — possible."
        fullHeight={false}
        alt="Round Table volunteers in orange t-shirts in front of a live brass band stage during Glossop Heritage Weekend 2026"
        captionTag="Heritage Weekend 2026"
      />

      <section className="py-16 sm:py-20 bg-white px-4">
        <div className="max-w-3xl mx-auto">
          <span className="text-xs uppercase tracking-[0.2em] font-bold text-[#F0A500] block mb-3">
            Thank you
          </span>
          <h2 className="text-3xl sm:text-4xl font-bold tracking-tight text-[#0A0A0A] mb-6">
            Supported by Glossop&rsquo;s brilliant local businesses
          </h2>
          <div className="space-y-4 text-base text-[#52525B] leading-relaxed">
            <p>
              Our Bonfire Night, Heritage Weekend and GlossFest events are only possible because of the local firms who back us year after year &mdash; through event sponsorship, raffle prizes, in-kind help or simply spreading the word.
            </p>
            <p>
              Every pound they contribute goes directly to the local good causes we support. If you&rsquo;re a Glossopdale business and would like to get involved with a future event, please drop us a line via the <a href="/contact" className="text-[#F0A500] hover:text-[#D99500] font-semibold">Contact page</a>.
            </p>
          </div>
        </div>
      </section>

      <section className="pb-20 sm:pb-28 bg-white px-4">
        <div className="max-w-5xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            {SUPPORTERS.map((s) => (
              <SupporterCard key={s.name} supporter={s} />
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
